import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import debounce from 'lodash.debounce'
import { Stack } from '@mui/material'
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import useMutation from '@/service-library/core-hooks/useMutation'
import { showErrorSnackbar } from '@/utils/snackbars'
import LargeHeading from '@/components/large-heading/LargeHeading'
import { FormTextField, PixyDocsForm } from '../forms'

type FormValues = {
  name: string
}

export default function ProfileDetails() {
  const { member } = useStytchMember()
  const stytch = useStytchB2BClient()

  const methods = useForm<FormValues>({
    defaultValues: {
      name: member?.name || '',
    },
    mode: 'onChange',
  })

  const {
    formState: { errors },
    subscribe,
    reset,
  } = methods

  const { mutateAsync: updateName, isPending } = useMutation({
    mutationFn: async (values: FormValues) => {
      await stytch.self.update({ name: values.name })
    },
    onError: () => {
      reset()
      showErrorSnackbar(
        'Unable to update your information. Please try again later.',
      )
    },
  })

  const debouncedUpdateName = useMemo(
    () => debounce(updateName, 250),
    [updateName],
  )

  const nameError = errors.name

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
        isDirty: true,
        isValid: true,
      },
      callback: ({ values, isDirty, isValid }) => {
        if (!isValid || !isDirty) {
          debouncedUpdateName.cancel()
          return
        }
        if (values.name.trim() !== member?.name) {
          debouncedUpdateName({
            name: values.name.trim(),
          })
        }
      },
    })
    return () => callback()
  }, [debouncedUpdateName, member?.name, subscribe])

  if (!member) return null

  return (
    <Stack spacing={4} sx={{ height: '100%' }}>
      <LargeHeading heading={member.name} subHeading="User Profile" />
      <PixyDocsForm methods={methods} onSubmit={() => {}}>
        <FormTextField
          name="name"
          autoFocus
          helperText={nameError ? nameError.message : ''}
          error={!!nameError}
          label="Name"
          fullWidth
          required
          requiredErrorText="Name cannot be empty."
          useControllerProps={{
            rules: {
              validate: (value: string) => {
                if (!value.trim().length) return 'Name cannot be empty.'
                return true
              },
            },
          }}
          onBlur={() => {
            if (!isPending) {
              reset({
                name: member.name,
              })
            }
          }}
          sx={{
            maxWidth: 350,
          }}
        />
      </PixyDocsForm>
    </Stack>
  )
}
